import { memo, useMemo, useState } from 'react';
import { useLookupObject } from 'react-scale/corp/views/CorpProjectGroupManager/communication/apiClient';
import { type UnresolvedPipelineV3NodeId } from '@scale/task-builder-shared';
import { ITaskReturnObject } from '@scale/shared/types/return_object/task';
import { useGetCustomerAuditTask } from 'react-scale/corp/components/ChatTaskFormatter/deliveryBuilderApiClient';
import { ReadOnlyGenericTaskWithResponse } from 'react-scale/corp/views/qm/QMLLMWorkerPerformancePage/QCDisputeFlow/ReadOnlyGenericTaskWithResponse';
import { TaskParamsVersion } from './types';
import _ from 'lodash';
type ReadOnlyGenericTaskProps = {
  taskIdOrtaskAttemptId: string;

  // This is to be able to support receiving the object directly from the lookup
  // without having to fetch the generic lookup object
  taskToRender?: ITaskReturnObject;

  headerClassName?: string;
  defaultViewAsCustomer?: boolean;
  extras?: {
    shouldShowContributorActions?: boolean;
    transactionHistoryId?: UnresolvedPipelineV3NodeId;
  };
  overrideReviewLevel?: number;
  setOverrideReviewLevel?: (reviewLevel: number | undefined) => void;
  taskParamsVersion?: TaskParamsVersion;
  setTaskParamsVersion?: (version: TaskParamsVersion) => void;
};

// TODO Not doing this in this PR as it would add a lot of complexity to review the code
// But we have to move this files under Operator App Components folder as this is now use for many apps/tools
export const ReadOnlyGenericTask = memo(
  ({
    taskIdOrtaskAttemptId,
    extras = {},
    defaultViewAsCustomer = false,
    headerClassName = '',
    taskToRender,
    overrideReviewLevel,
    setOverrideReviewLevel,
    taskParamsVersion,
    setTaskParamsVersion,
  }: ReadOnlyGenericTaskProps) => {
    const [customerView, setCustomerView] = useState(defaultViewAsCustomer);
    const [localTaskParamsVersion, setLocalTaskParamsVersion] =
      useState<TaskParamsVersion>('most-recent');

    // We fetch it even if the current view is not as customer at first to have a better UX in terms of loading times
    const {
      data: { task: customerAuditTask = undefined } = {},
      isLoading: isLoadingCustomerAuditTask,
    } = useGetCustomerAuditTask({ taskId: taskIdOrtaskAttemptId });

    const { data: lookupObject, isLoading: isLoadingLookupObject } = useLookupObject(
      taskIdOrtaskAttemptId,
      _.isNil(taskToRender),
    );

    const { attemptForTask, isLoadingAttemptedForTask } = useMemo(() => {
      const contributorTask = taskToRender ?? lookupObject;
      const isLoadingAttemptedForTask = taskToRender ? false : isLoadingLookupObject;

      return {
        attemptForTask: customerView ? customerAuditTask ?? contributorTask : contributorTask,
        isLoadingAttemptedForTask: customerView
          ? isLoadingCustomerAuditTask
          : isLoadingAttemptedForTask,
      };
    }, [
      customerAuditTask,
      lookupObject,
      customerView,
      isLoadingLookupObject,
      isLoadingCustomerAuditTask,
      taskToRender,
    ]);

    return (
      <ReadOnlyGenericTaskWithResponse
        key={`${attemptForTask?._id}-${customerView ? '-customerView' : '-defaultView'}`}
        extras={extras}
        headerClassName={headerClassName}
        attemptForTask={attemptForTask}
        isLoadingAttemptedForTask={isLoadingAttemptedForTask}
        setCustomerView={setCustomerView}
        customerView={customerView}
        overrideReviewLevel={overrideReviewLevel}
        setOverrideReviewLevel={setOverrideReviewLevel}
        taskParamsVersion={taskParamsVersion || localTaskParamsVersion}
        setTaskParamsVersion={setTaskParamsVersion || setLocalTaskParamsVersion}
      />
    );
  },
);
